import React, { useEffect, useState, useRef } from "react";
import { Card, ProgressBar, Spinner } from "react-bootstrap";
import { FaClock } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleRight } from "@fortawesome/free-solid-svg-icons";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import './Courses.css';
import CourseImage from "./images/CourseImage.png";
import { useApiLoading } from "../StudentDashboard";

interface Course {
  title: string;
  subject: string;
  subject_id: string;
  color: string;
  image: string;
  duration: string;
  progress: {
    student_progress: number;
    progress: number;
  };
  student_progress: number;
  status: string;
}

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [scrollPosition, setScrollPosition] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleCourseClick = (subject_id: string, subject: string, courseTitle: string) => {
    // Store subject data directly without encryption
    sessionStorage.setItem('SubjectId', subject_id);
    sessionStorage.setItem("Subject", subject);
    // Navigation removed - no longer navigates to SubjectOverview
  };

  // Use the context instead of making our own API call
  const { coursesData, coursesError, isCoursesApiLoaded: coursesLoaded } = useApiLoading();

  useEffect(() => {
    if (coursesData && coursesLoaded) {
      const availableColors = ["#B6BAFE","#F0DC54","#B5FEB5","#B6FEB5","#B6BAFE","#FFB5B5"];

      const courseData = (coursesData?.subjects || []).map((course: any, index: number) => ({
        ...course,
        color: availableColors[index % availableColors.length],
      }));

      setCourses(courseData);

      setTimeout(checkScrollStatus, 100);
    }
  }, [coursesData, coursesLoaded]);

  const checkScrollStatus = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      const hasRightScroll = Math.ceil(scrollLeft + clientWidth) < scrollWidth - 2;

      setScrollPosition({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: hasRightScroll
      });
    }
  };

  useEffect(() => {
    checkScrollStatus();

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollStatus);

      setTimeout(checkScrollStatus, 100);

      window.addEventListener('resize', checkScrollStatus);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollStatus);
      }
      window.removeEventListener('resize', checkScrollStatus);
    };
  }, [courses.length]);

  const scrollLeft = () => {
    if (scrollContainerRef.current && scrollPosition.canScrollLeft) {
      const container = scrollContainerRef.current;
      container.scrollBy({
        left: -container.clientWidth / 2,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current && scrollPosition.canScrollRight) {
      const container = scrollContainerRef.current;
      container.scrollBy({
        left: container.clientWidth / 2,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="row rounded-2">
      <div className="d-flex justify-content-end py-1" style={{ fontSize: "14px" }}>
        <div className="text-dark">
          <ChevronLeft
            onClick={scrollLeft}
            className={`arrow left ${
              !scrollPosition.canScrollLeft ? "disabled" : ""
            }`}
            style={{
              cursor: scrollPosition.canScrollLeft ? "pointer" : "pointer",
              color: !scrollPosition.canScrollLeft ? "#7F7F7F" : "inherit",
            }}
          />
          <ChevronRight
            onClick={scrollRight}
            className={`arrow right ${
              !scrollPosition.canScrollRight ? "disabled" : ""
            }`}
            style={{
              cursor: scrollPosition.canScrollRight ? "pointer" : "pointer",
              color: !scrollPosition.canScrollRight ? "#7F7F7F" : "inherit",
            }}
          />
        </div>
      </div>
      <div
        className="course-container d-flex text-dark pb-3 pt-1 flex-nowrap"
        ref={scrollContainerRef}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          width: "100%",
        }}
        onLoad={checkScrollStatus}
      >
        {!courses.length ? (
          <div className="d-flex justify-content-center align-items-center w-100 py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading courses...</span>
            </Spinner>
          </div>
        ) : (
          courses.map((course, index) => (
            <Card
              key={index}
              className={`mb-2`}
              style={{
                width: "350px",
                height: "150px",
                minWidth: "350px",
                marginRight: index !== courses.length - 1 ? "1rem" : "0",
                boxShadow: "8px 8px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Card.Body className="p-1 rounded-2">
                <div className="d-flex p-0">
                  <div className="d-flex flex-column align-items-center me-3">
                    <div
                      className="preview-image ms-2 mt-1 mb-2 border rounded-2"
                      style={{
                        width: "90px",
                        height: "130px",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={course.image}
                        alt={`${course.title} Preview`}
                        className="img"
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-column">
                    <h6
                      className="p-1 mt-2 me-3"
                      style={{
                        backgroundColor: course.color,
                        color: "black",
                        borderRadius: "4px",
                        width: "200px",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        justifyContent: "space-between",
                        cursor: "pointer"
                      }}
                      title={course.title.length > 20 ? course.title : ""}
                      onClick={() => {
                        if (course.status === 'Open') {
                          handleCourseClick(course.subject_id, course.subject, course.title);
                        }
                      }}
                    >
                      {course.title.length > 20
                        ? `${course.title.slice(0, 20)}...`
                        : course.title}
                      <FontAwesomeIcon
                        icon={faCircleRight}
                        style={{
                          color: "#009dff",
                          fontSize: "18px"
                        }}
                      />
                    </h6>
                    <span style={{ fontSize: "14px", color: "black" }}>
                      <span>
                        <FaClock className="mb-1" />
                        <span className="text-secondary ps-2">Duration</span>
                      </span>
                      <br />
                      <span>{course.duration}</span>
                      <ProgressBar
                        className="me-3 border-dark border rounded-1"
                        style={{
                          flexGrow: 1,
                          marginTop: "30px",
                          height: "10px",
                          borderRadius: "4px",
                          borderColor: "#74C0FC",
                          backgroundColor: "white",
                        }}
                      >
                        <ProgressBar
                          style={{ color: "#37D447", background: "#37D447" }}
                          now={course.progress.student_progress}
                          key={1}
                        />
                        <ProgressBar
                          now={course.progress.progress - course.progress.student_progress}
                          key={2}
                          style={{ backgroundColor: "white" }}
                        />
                      </ProgressBar>
                      {course.progress.student_progress === 0 && (
                        <span className="d-flex justify-content-center align-items-center text-center pt-1" style={{ fontSize: "8px" }}>
                          Not Started
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Courses;
